import React from 'react';
import { Button, Tooltip, Typography, message } from 'antd';
import { motion } from 'framer-motion';
import { RightOutlined } from '@ant-design/icons';
// colors not needed here; keep imports minimal

// Visual-first gallery card for workflow jobs, matching ImageJobItem gallery design
export function WorkflowGalleryItem({ job, onJobClick }) {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isCopyHovered, setIsCopyHovered] = React.useState(false);

  const copyToClipboard = async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(String(text));
      } else {
        const ta = document.createElement("textarea");
        ta.value = String(text);
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      message.success('Text copied to clipboard!');
    } catch (error) {
      message.error('Failed to copy text');
    }
  };

  const resultsArr = Array.isArray(job?.results?.results) ? job.results.results : [];
  const imageResult = resultsArr.find(
    (r) => (r?.external_url || r?.content) && (r?.content_type === 'image' || !r?.content_type)
  );
  const videoResult = resultsArr.find(
    (r) => (r?.external_url || r?.content) && r?.content_type === 'video'
  );
  const textResult = job.status === 'completed'
    ? resultsArr.find((r) => r?.content && r?.content_type === 'text')
    : null;
  const hasImage = job.status === 'completed' && !!imageResult;
  const hasVideo = job.status === 'completed' && !!videoResult;
  const hasText = job.status === 'completed' && !!textResult;
  const pillName = job?.workflow_definition_name || job?.workflow_name || job?.model_name;

  return (
    <motion.div
      role="button"
      onClick={() => onJobClick && onJobClick(job)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: 'transparent',
        border: 'none',
        borderRadius: 0,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        position: 'relative',
      }}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Preview area */}
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          lineHeight: 0,
          height: 0,
          paddingTop: '100%', // square aspect ratio
        }}
      >
        {hasImage ? (
          <img
            src={imageResult.external_url || imageResult.content}
            alt=""
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              display: 'block',
              objectFit: 'cover',
              transition: 'filter 0.2s ease',
              filter: isHovered ? 'blur(2px) brightness(0.7)' : 'none',
            }}
          />
        ) : hasVideo ? (
          <video
            src={videoResult.external_url || videoResult.content}
            muted
            playsInline
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : hasText ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(59, 130, 246, 0.15))",
              display: "flex",
              flexDirection: "column",
              padding: "8px",
              paddingTop: "28px",
              filter: (isHovered && !isCopyHovered) ? "blur(2px) brightness(0.7)" : "none",
              transition: "filter 0.2s ease"
            }}
          >
            {/* Text icon - smaller header */}
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 3,
              marginBottom: 3
            }}>
              <span style={{ fontSize: 12 }}>📝</span>
              <Typography.Text style={{ 
                fontSize: 8, 
                fontWeight: 600,
                color: "#22c55e"
              }}>
                TEXT
              </Typography.Text>
            </div>
            
            {/* Text preview - much bigger */}
            <div style={{ 
              flex: 1,
              display: "flex",
              flexDirection: "column",
              paddingRight: 28,
              paddingBottom: 32
            }}>
              <Typography.Text 
                style={{ 
                  fontSize: 10, 
                  color: "rgba(255,255,255,0.95)",
                  lineHeight: 1.3,
                  display: "-webkit-box",
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}
              >
                {textResult?.content}
              </Typography.Text>
            </div>
            
            {/* Copy button */}
            <div
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(textResult?.content || '');
              }}
              onMouseEnter={(e) => {
                e.stopPropagation();
                setIsCopyHovered(true);
              }}
              onMouseLeave={(e) => {
                e.stopPropagation();
                setIsCopyHovered(false);
              }}
              style={{
                position: "absolute",
                bottom: 6,
                right: 6,
                width: 20,
                height: 20,
                borderRadius: 3,
                background: isCopyHovered ? "rgba(34, 197, 94, 0.5)" : "rgba(34, 197, 94, 0.3)",
                border: "1px solid rgba(34, 197, 94, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
                zIndex: 10
              }}
            >
              <span style={{ fontSize: 10 }}>📋</span>
            </div>
          </div>
        ) : (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                job.status === 'failed' ? 'rgba(255, 77, 79, 0.12)' : 'rgba(255,255,255,0.06)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: job.status === 'failed' ? '#ff4d4f' : 'rgba(255,255,255,0.75)',
              fontSize: 13,
            }}
            title={job.status === 'failed' && job.error ? String(job.error) : undefined}
          >
            <span>
              {job.status === 'processing' || job.status === 'pending'
                ? 'Processing…'
                : job.status === 'failed'
                ? 'Failed'
                : 'Queued'}
            </span>
          </div>
        )}

        {/* Top-right pill with workflow/model name */}
        {pillName && (
          <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 2, maxWidth: 'calc(100% - 16px)' }}>
            <Tooltip title={pillName}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: 'linear-gradient(135deg, #1890ff, #69c0ff)',
                  color: '#fff',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  backdropFilter: 'blur(2px)',
                }}
              >
                <span
                  style={{
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: '18px',
                  }}
                >
                  {pillName}
                </span>
              </div>
            </Tooltip>
          </div>
        )}

        {/* Hover overlay with single 'More info' button */}
        {(hasImage || hasVideo || hasText) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: isHovered ? 'auto' : 'none',
            }}
          >
            <Button
              size="middle"
              type="primary"
              ghost
              shape="round"
              icon={<RightOutlined />}
              style={{ color: '#fff', borderColor: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.35)', backdropFilter: 'blur(2px)' }}
              onClick={(e) => {
                e.stopPropagation();
                onJobClick && onJobClick(job);
              }}
            >
              More info
            </Button>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

export default WorkflowGalleryItem;
